/**
 * useContactPayments — F-F.2
 *
 * Paginierte Zahlungen eines Contacts laden.
 * GET /api/v2/contacts/:contact_id/payments?page=1&per_page=10
 *
 * FIX #33: das BE liefert {id, amount, currency, time, memo, tx_hash, type}
 * (siehe Api::V2::ContactsController#payments) — nicht period_start/
 * period_end/status/paid_at wie hier vorher angenommen. Ursache fuer
 * "Invalid Date" in der Personen-Detail-Ansicht. Jetzt Typ + Tabelle auf die
 * echten Felder umgestellt.
 *
 * Der Endpoint ist jetzt Teil der OpenAPI-Spec (api-types/openapi.yaml,
 * operationId listApiV2ContactPayments) — deshalb Umstellung auf den
 * generierten apiClient (identisches Muster zu useContacts.ts), kein
 * direkter fetch() mehr noetig.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import { useState } from 'react'

export type Payment = {
  id: number
  amount: number
  currency: string
  time: string | null
  memo: string | null
  tx_hash: string | null
  type: string
}

type PagyMeta = {
  page: number
  pages: number
  count: number
  items: number
}

type PaymentsResponse = {
  data: Payment[]
  meta: { pagy: PagyMeta }
}

export function useContactPayments(contactId: string) {
  const [page, setPage] = useState(1)
  const [allPayments, setAllPayments] = useState<Payment[]>([])
  const [isUnavailable, setIsUnavailable] = useState(false)

  const query = useApi<PaymentsResponse | null>(
    ['contact-payments', contactId, page],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/contacts/{contact_id}/payments', {
        params: {
          path: { contact_id: Number(contactId) },
          query: { page, per_page: 10 },
        },
      })

      if (result.response?.status === 404) {
        setIsUnavailable(true)
        return { data: null }
      }

      if (result.response && result.response.status >= 500) {
        throw { status: result.response.status }
      }

      setIsUnavailable(false)
      const body = result.data as PaymentsResponse
      if (page === 1) {
        setAllPayments(body.data)
      } else {
        setAllPayments((prev) => [...prev, ...body.data])
      }
      return { data: body }
    },
    { retry: false, staleTime: 30_000 },
  )

  const meta = query.data?.meta?.pagy
  const hasMore = meta != null ? page < meta.pages : false

  function loadMore() {
    if (hasMore) {
      setPage((p) => p + 1)
    }
  }

  return {
    payments: allPayments,
    isLoading: query.isLoading,
    isError: query.isError,
    isUnavailable,
    hasMore,
    loadMore,
  }
}
